import { jsonify, sanitize } from "../TapiocaUtils";
export default class CapiClient {
  constructor(raw) {
    this.raw = raw;
  }
  /**
   * Add an annotation to a sheet
   */
  addAnnotation(projectUid, body) {
    const url = `/projects/${projectUid}/annotations`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Add annotations in bulk
   */
  addBulkAnnotations(projectUid, body) {
    const url = `/projects/${projectUid}/annotations_/create`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Webhook endpoint that completes the upload and finalizes the temporary
   * record. Does not require an API key. Calculates size of file for the API
   * user (required in the record).
   * Details here: https://plangrid.quip.com/6WdKAyvbuiT9
   */
  completeAttachmentUpload(projectUid, uploadToken) {
    const url = `/projects/${projectUid}/attachments/uploads/completions/${uploadToken}`;
    return this.raw.get(url);
  }
  /**
   * Webhook endpoint that completes the upload and finalizes the temporary
   * record. Does not require an API key. Generates thumbnail and calculates
   * size of file for the API user (required in the record).
   * Details here: https://plangrid.quip.com/6WdKAyvbuiT9
   */
  completePhotoUpload(projectUid, uploadToken) {
    const url = `/projects/${projectUid}/photos/uploads/completions/${uploadToken}`;
    return this.raw.get(url);
  }
  /**
   * Webhook endpoint that completes the file upload. Does not require an API key.
   * Details here: https://plangrid.quip.com/6WdKAyvbuiT9
   */
  completeSheetUpload(projectUid, uploadToken) {
    const url = `/projects/${projectUid}/sheets/uploads/files/completions/${uploadToken}`;
    return this.raw.get(url);
  }
  /**
   * Copy annotations to other sheets
   */
  copyAnnotations(projectUid, body) {
    const url = `/projects/${projectUid}/annotations_/copy`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Starts the process of creating an attachment: creates a temporary record
   * from metadata provided by API user and provides API user with AWS post
   * form arguments.
   * API user specifies the content-type of the attachment so the S3 upload
   * packet contains the appropriate content-type header. The mime-type of
   * the file is also verified.
   * Once uploaded, AWS tells the customer API to complete the record: check
   * whether a file has been uploaded (fails if it hasn't), calculate size
   * for the API user (required in the record), and set the created_at to
   * the time of the first API call.
   * Details here: https://plangrid.quip.com/6WdKAyvbuiT9
   */
  createAttachmentUpload(projectUid, body) {
    const url = `/projects/${projectUid}/attachments/uploads`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a document. At present, only used by kloudless attachment in workers.
   */
  createDocument(projectUid, body) {
    const url = `/projects/${projectUid}/documents`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Starts the process of exporting a group of field reports.
   */
  createFieldReportsExport(projectUid, body) {
    const url = `/projects/${projectUid}/field_reports/export`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Initiate an clone request from a source field report type to a target field report type.
   */
  createFieldReportTypeCloneRequest(targetProjectUid, body) {
    const url = `/projects/${targetProjectUid}/field_report_types/create_clone`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new group
   */
  createGroup(projectUid, body) {
    const url = `/projects/${projectUid}/groups`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Add a new user to the group
   */
  createGroupMember(projectUid, groupUid, body) {
    const url = `/projects/${projectUid}/groups/${groupUid}/members`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Add new users to the group
   */
  createGroupMembers(projectUid, groupUid, body) {
    const url = `/projects/${projectUid}/groups/${groupUid}/members_/create`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create an issue
   */
  createIssue(projectUid, body) {
    const url = `/projects/${projectUid}/issues`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Add a comment on issue.
   */
  createIssueComment(projectUid, issueUid, body) {
    const url = `/projects/${projectUid}/issues/${issueUid}/comment`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new OAuth application.
   */
  createOauthApplication(body) {
    const url = "/oauth/applications";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a new group, or update it if it already exists
   */
  createOrUpdateGroup(projectUid, groupUid, body) {
    const url = `/projects/${projectUid}/groups/${groupUid}`;
    return this.raw.put(url, jsonify(body));
  }
  /**
   * Starts the process of creating a photo: creates a temporary record
   * from metadata provided by API user and provides API user with AWS post
   * form arguments.
   * API user specifies the content-type of the photo so the S3 upload
   * packet contains the appropriate content-type header. The mime-type of
   * the file is also verified.
   * Once uploaded, AWS tells the customer API to complete the record: check
   * whether a file has been uploaded (fails if it hasn't), calculate size
   * for the API user (required in the record), and set the created_at to
   * the time of the first API call.
   * Details here: https://plangrid.quip.com/6WdKAyvbuiT9
   */
  createPhotoUpload(projectUid, body) {
    const url = `/projects/${projectUid}/photos/uploads`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a project and add API user as an team admin.
   */
  createProject(body) {
    const url = "/projects";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a restricted user on a project or do nothing if restricted user already exists.
   */
  createProjectRestrictedUser(projectUid, body) {
    const url = `/projects/${projectUid}/restricted_users`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create an RFI. API user is set as creator and all notifications are sent.
   */
  createRfi(projectUid, body) {
    const url = `/projects/${projectUid}/rfis`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Adds an existing attachment as an RFI reference. If the attachment has
   * already been added to the RFI reference, the API does nothing and returns a
   * 200.
   */
  createRfiAttachmentReference(projectUid, rfiUid, body) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/attachments`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Adds an existing photo as an RFI reference. If the photo has already
   * been added to the RFI reference, the API does nothing and returns a
   * 200.
   */
  createRfiPhotoReference(projectUid, rfiUid, body) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/photos`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Requests a clone of a project according to sample project guidelines.
   * After clone request is complete, project is sanitized according to
   * sample project guidelines. API User must have admin (staff-user) access.
   */
  createSampleProject(body) {
    const url = "/sample_projects";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Starts the process of exporting a group of sheets.
   * Details here: https://plangrid.quip.com/aV9IAR8p58a7
   */
  createSheetPacket(projectUid, body) {
    const url = `/projects/${projectUid}/sheets/packets`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Starts the process of uploading a group of sheets. API user specifies
   * the version name (which carries over into the Web staging area) and the
   * number of sheets to be uploaded. The API returns the same number of file
   * upload request objects in an array.
   * Details here: https://plangrid.quip.com/6WdKAyvbuiT9
   */
  createSheetUpload(projectUid, body) {
    const url = `/projects/${projectUid}/sheets/uploads`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a version set for the given project
   */
  createVersionSet(projectUid, body) {
    const url = `/projects/${projectUid}/version_sets`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create a workspace with projected attachments/sheets. API user is set as admin.
   */
  createWorkspace(body) {
    const url = "/workspaces";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Deactivate an organization's OAuth tokens.
   */
  deactivateOrganizationOauthTokens(organizationId) {
    const url = `/oauth/deactivate/organizations/${organizationId}`;
    return this.raw.post(url, {});
  }
  /**
   * Deactivate a user's OAuth tokens.
   */
  deactivateUserOauthTokens(userId) {
    const url = `/oauth/deactivate/users/${userId}`;
    return this.raw.post(url, {});
  }
  /**
   * Soft deletes an annotation. If annotation has already been deleted,
   * return 204.
   */
  deleteAnnotation(projectUid, annotationUid, params) {
    const url = {
      url: `/projects/${projectUid}/annotations/${annotationUid}`,
      params: sanitize(params || {}),
    };
    return this.raw.delete(url);
  }
  /**
   * Soft deletes an attachment. If attachment has already been deleted,
   * return 204.
   */
  deleteAttachment(projectUid, attachmentUid) {
    const url = `/projects/${projectUid}/attachments/${attachmentUid}`;
    return this.raw.delete(url);
  }
  /**
   * Delete annotations in bulk
   */
  deleteBulkAnnotations(projectUid, body) {
    const url = `/projects/${projectUid}/annotations_/delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Soft delete a group. If group has already been deleted return 204
   */
  deleteGroup(projectUid, groupUid) {
    const url = `/projects/${projectUid}/groups/${groupUid}`;
    return this.raw.delete(url);
  }
  /**
   * Delete a group member
   */
  deleteGroupMember(projectUid, groupUid, userUid) {
    const url = `/projects/${projectUid}/groups/${groupUid}/members/${userUid}`;
    return this.raw.delete(url);
  }
  /**
   * Remove users from the group
   */
  deleteGroupMembers(projectUid, groupUid, body) {
    const url = `/projects/${projectUid}/groups/${groupUid}/members_/delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Remove issue list from a project. If the issue list doesn't exist in the project,
   * return 404.
   */
  deleteIssueList(projectUid, issueListUid) {
    const url = `/projects/${projectUid}/issue_lists/${issueListUid}`;
    return this.raw.delete(url);
  }
  /**
   * Delete an OAuth application. If the application doesn't exist or has already been deleted a 404 will be returned.
   */
  deleteOauthApplication(clientId) {
    const url = `/oauth/applications/${clientId}`;
    return this.raw.delete(url);
  }
  /**
   * Soft deletes photo in a project. If photo has already been deleted,
   * return 204.
   */
  deletePhoto(projectUid, photoUid) {
    const url = `/projects/${projectUid}/photos/${photoUid}`;
    return this.raw.delete(url);
  }
  /**
   * Remove a restricted user from the project. API user must have permissions to remove
   * project team members (see [Spec] Permissions, currently only admins can
   * remove).
   */
  deleteProjectRestrictedUser(projectUid, userId) {
    const url = `/projects/${projectUid}/restricted_users/${userId}`;
    return this.raw.delete(url);
  }
  /**
   * Remove a user from the project team. If user has already been removed
   * a 204 will still be returned. API user must have permissions to remove
   * project team members (see [Spec] Permissions, currently only admins can
   * remove).
   */
  deleteProjectUser(projectUid, userUid) {
    const url = `/projects/${projectUid}/users/${userUid}`;
    return this.raw.delete(url);
  }
  /**
   * Delete an RFI in a project
   */
  deleteRfi(projectUid, rfiUid) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}`;
    return this.raw.delete(url);
  }
  /**
   * Remove attachment reference from RFI. If attachment is not referenced in RFI,
   * return 404.
   */
  deleteRfiAttachmentReference(projectUid, rfiUid, attachmentUid) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/attachments/${attachmentUid}`;
    return this.raw.delete(url);
  }
  /**
   * Remove photo reference from RFI. If photo is not referenced in RFI,
   * return 404.
   */
  deleteRfiPhotoReference(projectUid, rfiUid, photoUid) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/photos/${photoUid}`;
    return this.raw.delete(url);
  }
  /**
   * Remove snapshot reference from RFI. If snapshot is not referenced in RFI,
   * return 404.
   */
  deleteRfiSnapshotReference(projectUid, rfiUid, snapshotUid) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/snapshots/${snapshotUid}`;
    return this.raw.delete(url);
  }
  /**
   * Soft deletes snapshot in a project. If snapshot has already been deleted,
   * return 204.
   */
  deleteSnapshot(projectUid, snapshotUid) {
    const url = `/projects/${projectUid}/snapshots/${snapshotUid}`;
    return this.raw.delete(url);
  }
  /**
   * Remove a user from projects
   */
  deleteUserFromProjects(userId, body) {
    const url = `/users/${userId}/projects_/delete`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Remove  users from project list
   */
  deleteUsersFromProjects(body) {
    const url = "/projects_/users/delete";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Filter a list of containers the user has access to.
   */
  filterContainerGroups(userId, body) {
    const url = `/container_groups/${userId}`;
    return this.raw.post(url, jsonify(body));
  }
  getAllProjectRestrictedUsers(
    projectUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/restricted_users`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getAnnotations(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/annotations`;
    if (options.getAll) {
      return this.raw.getAll(
        url,
        sanitize(params || {}, {
          ann_ignore_types: "csv",
          ann_uids: "csv",
          extra_fields: "csv",
          punch_uids: "csv",
          sheet_uids: "csv",
        })
      );
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, {
        ann_ignore_types: "csv",
        ann_uids: "csv",
        extra_fields: "csv",
        punch_uids: "csv",
        sheet_uids: "csv",
      }),
    });
  }
  /**
   * Retrieve an attachment by uid.
   */
  getAttachment(projectUid, attachmentUid) {
    const url = `/projects/${projectUid}/attachments/${attachmentUid}`;
    return this.raw.get(url);
  }
  getAttachments(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/attachments`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, { uids: "csv" }));
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, { uids: "csv" }),
    });
  }
  /**
   * Get all attachments projected into a workspace
   */
  getAttachmentsProjectedIntoWorkspace(workspaceUid) {
    const url = `/workspaces/${workspaceUid}/attachments`;
    return this.raw.get(url);
  }
  /**
   * Returns the token user's profile
   */
  getAuthenticatedUserProfile() {
    const url = "/me";
    return this.raw.get(url);
  }
  /**
   * Get the user and token information associated with a token value.
   */
  getBasicToken(token) {
    const url = `/authentication_tokens/basic/${token}`;
    return this.raw.get(url);
  }
  /**
   * Get the user and token information associated with a token value.
   */
  getBearerToken(token) {
    const url = `/authentication_tokens/bearer/${token}`;
    return this.raw.get(url);
  }
  /**
   * Get the user and token information associated with a token value.
   */
  getCacahueteToken(token) {
    const url = `/authentication_tokens/cacahuete/${token}`;
    return this.raw.get(url);
  }
  getComments(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/comments`;
    if (options.getAll) {
      return this.raw.getAll(
        url,
        sanitize(params || {}, { record_types: "csv" })
      );
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, { record_types: "csv" }),
    });
  }
  /**
   * Retrieve a document by uid.
   */
  getDocument(projectUid, documentUid) {
    const url = `/projects/${projectUid}/documents/${documentUid}`;
    return this.raw.get(url);
  }
  getDocuments(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/documents`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, { uids: "csv" }));
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, { uids: "csv" }),
    });
  }
  /**
   * Returns status of the specified export.
   */
  getFieldReportExport(projectUid, stapleUid) {
    const url = `/projects/${projectUid}/field_reports/export/${stapleUid}`;
    return this.raw.get(url);
  }
  getFieldReportIssues(
    projectUid,
    reportUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/projects/${projectUid}/field_reports/${reportUid}/issues`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getFieldReports(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/field_reports`;
    if (options.getAll) {
      return this.raw.getAll(
        url,
        sanitize(params || {}, {
          location_ids: "csv",
          statuses: "csv",
          uids: "csv",
        })
      );
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, {
        location_ids: "csv",
        statuses: "csv",
        uids: "csv",
      }),
    });
  }
  getFieldReportTemplates(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/field_report_templates`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Returns status of the specified clone operation.
   */
  getFieldReportTypeCloneOperationStatus(cloneUid, params) {
    const url = {
      url: `/field_report_types/get_clone_status/${cloneUid}`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * Get a group
   */
  getGroup(projectUid, groupUid) {
    const url = `/projects/${projectUid}/groups/${groupUid}`;
    return this.raw.get(url);
  }
  getGroupMembers(projectUid, groupUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/groups/${groupUid}/members`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getGroups(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/groups`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, { uids: "csv" }));
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, { uids: "csv" }),
    });
  }
  /**
   * Returns an issue in a project.
   */
  getIssue(projectUid, issueUid) {
    const url = `/projects/${projectUid}/issues/${issueUid}`;
    return this.raw.get(url);
  }
  getIssueComments(projectUid, issueUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/issues/${issueUid}/comments`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Returns an issue list in a project.
   */
  getIssueList(projectUid, issueListUid) {
    const url = `/projects/${projectUid}/issue_lists/${issueListUid}`;
    return this.raw.get(url);
  }
  getIssueLists(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/issue_lists`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getIssuePhotos(projectUid, issueUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/issues/${issueUid}/photos`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getIssues(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/issues`;
    if (options.getAll) {
      return this.raw.getAll(
        url,
        sanitize(params || {}, {
          group_assignees: "csv",
          group_followers: "csv",
          issue_list_uids: "csv",
          issue_uids: "csv",
          issue_uids_option: "csv",
          sheet_uids: "csv",
          stamp_seq_range: "csv",
          user_assignees: "csv",
          user_followers: "csv",
        })
      );
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, {
        group_assignees: "csv",
        group_followers: "csv",
        issue_list_uids: "csv",
        issue_uids: "csv",
        issue_uids_option: "csv",
        sheet_uids: "csv",
        stamp_seq_range: "csv",
        user_assignees: "csv",
        user_followers: "csv",
      }),
    });
  }
  /**
   * Get number of unread comments by a user for a given project and issue.
   */
  getIssueUnreadCommentsCount(projectUid, issueUid) {
    const url = `/projects/${projectUid}/issues/${issueUid}/comments/unread_count`;
    return this.raw.get(url);
  }
  /**
   * Get a user's message acknowledgement.
   */
  getMessageAcknowledgement(userId, acknowledgementName) {
    const url = `/users/${userId}/message_acknowledgements/${acknowledgementName}`;
    return this.raw.get(url);
  }
  /**
   * Get an OAuth app's info.
   */
  getOauthApplication(clientId) {
    const url = `/oauth/applications/${clientId}`;
    return this.raw.get(url);
  }
  getOauthApplications(params, options = { getAll: false }) {
    const url = "/oauth/applications";
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Retrieve photo in a project.
   */
  getPhoto(projectUid, photoUid) {
    const url = `/projects/${projectUid}/photos/${photoUid}`;
    return this.raw.get(url);
  }
  getPhotos(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/photos`;
    if (options.getAll) {
      return this.raw.getAll(
        url,
        sanitize(params || {}, { extra_fields: "csv", photo_uids: "csv" })
      );
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, {
        extra_fields: "csv",
        photo_uids: "csv",
      }),
    });
  }
  /**
   * Returns a project matching the supplied unique identifier.
   */
  getProject(projectUid) {
    const url = `/projects/${projectUid}`;
    return this.raw.get(url);
  }
  getProjectAcsActivity(projectUid, userId) {
    const url = `/projects/${projectUid}/acs-activity/${userId}`;
    return this.raw.get(url);
  }
  /**
   * Returns a restricted users on a project.
   */
  getProjectRestrictedUser(projectUid, userId, params) {
    const url = {
      url: `/projects/${projectUid}/restricted_users/${userId}`,
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  getProjects(params, options = { getAll: false }) {
    const url = "/projects";
    if (options.getAll) {
      return this.raw.getAll(
        url,
        sanitize(params || {}, { extra_fields: "csv" })
      );
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, { extra_fields: "csv" }),
    });
  }
  getProjectSheetOrder(projectUid) {
    const url = `/projects/${projectUid}/sheet_order`;
    return this.raw.get(url);
  }
  /**
   * Returns a user on a project team. Removed users are returned with 'removed' set to true.
   */
  getProjectUser(projectUid, userUid, params) {
    const url = {
      url: `/projects/${projectUid}/users/${userUid}`,
      params: sanitize(params || {}, { extra_fields: "csv" }),
    };
    return this.raw.get(url);
  }
  /**
   * Returns an RFI in a project
   */
  getRfi(projectUid, rfiUid) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}`;
    return this.raw.get(url);
  }
  getRfiAttachments(projectUid, rfiUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/attachments`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getRfiComments(projectUid, rfiUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/comments`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getRfiHistory(projectUid, rfiUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/history`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getRfiPhotos(projectUid, rfiUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/photos`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getRfis(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/rfis`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getRfiSnapshots(projectUid, rfiUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}/snapshots`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Returns an RFI status on a project
   */
  getRfiStatus(projectUid, statusUid) {
    const url = `/projects/${projectUid}/rfis/statuses/${statusUid}`;
    return this.raw.get(url);
  }
  getRfiStatuses(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/rfis/statuses`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Returns a role on a project.
   */
  getRole(projectUid, roleUid) {
    const url = `/projects/${projectUid}/roles/${roleUid}`;
    return this.raw.get(url);
  }
  getRoles(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/roles`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getRootCauses(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/root_causes`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Kick off the login flow.
   */
  getSamlInitialization(params) {
    const url = {
      url: "/sessions/auth/init",
      params: sanitize(params || {}),
    };
    return this.raw.get(url);
  }
  /**
   * See status of API User must have admin (staff-user) access.
   */
  getSampleProjectStatus(sampleProjectUid) {
    const url = `/sample_projects/${sampleProjectUid}/status`;
    return this.raw.get(url);
  }
  /**
   * Retrieve a sheet in a project.
   */
  getSheet(projectUid, sheetUid, params) {
    const url = {
      url: `/projects/${projectUid}/sheets/${sheetUid}`,
      params: sanitize(params || {}, { extra_fields: "csv" }),
    };
    return this.raw.get(url);
  }
  getSheetHistorySetsProjectedIntoWorkspace(
    workspaceUid,
    params,
    options = { getAll: false }
  ) {
    const url = `/workspaces/${workspaceUid}/sheet_history_sets`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  /**
   * Returns status of the specified export.
   * Details here: https://plangrid.quip.com/aV9IAR8p58a7
   */
  getSheetPacket(projectUid, packetUid) {
    const url = `/projects/${projectUid}/sheets/packets/${packetUid}`;
    return this.raw.get(url);
  }
  getSheets(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/sheets`;
    if (options.getAll) {
      return this.raw.getAll(
        url,
        sanitize(params || {}, {
          history_set_uids: "csv",
          statuses: "csv",
          uids: "csv",
        })
      );
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, {
        history_set_uids: "csv",
        statuses: "csv",
        uids: "csv",
      }),
    });
  }
  /**
   * Retrieve FileUpload objects given the unique identifier of the request.
   * The file name on AWS is determined by the file_name parameter passed in.
   * Details here: https://plangrid.quip.com/6WdKAyvbuiT9
   */
  getSheetUploadFile(projectUid, versionUploadUid, fileUploadRequestUid, body) {
    const url = `/projects/${projectUid}/sheets/uploads/${versionUploadUid}/files/${fileUploadRequestUid}`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Retrieve snapshot in a project.
   */
  getSnapshot(projectUid, snapshotUid) {
    const url = `/projects/${projectUid}/snapshots/${snapshotUid}`;
    return this.raw.get(url);
  }
  getUserGroups(projectUid, userUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/users/${userUid}/groups`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, { uids: "csv" }));
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, { uids: "csv" }),
    });
  }
  getUsers(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/users`;
    if (options.getAll) {
      return this.raw.getAll(
        url,
        sanitize(params || {}, { extra_fields: "csv", uids: "csv" })
      );
    }
    return this.raw.get({
      url,
      params: sanitize(params || {}, { extra_fields: "csv", uids: "csv" }),
    });
  }
  getVersionSets(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/version_sets`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  getWorkspaces(projectUid, params, options = { getAll: false }) {
    const url = `/projects/${projectUid}/workspaces`;
    if (options.getAll) {
      return this.raw.getAll(url, sanitize(params || {}, {}));
    }
    return this.raw.get({ url, params: sanitize(params || {}, {}) });
  }
  guessProjectSheetOrder(projectUid) {
    const url = `/projects/${projectUid}/sheet_order/guess`;
    return this.raw.get(url);
  }
  /**
   * Add a user to the project team and send an email invite to join the
   * project. API user must have permissions to invite project team members
   * (see [Spec] Permissions, currently only admins can invite).
   */
  inviteUser(projectUid, body) {
    const url = `/projects/${projectUid}/users/invites`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Invite a user to PlanGrid (but not to a specific project).
   */
  inviteUserToPlangrid(body) {
    const url = "/invite";
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Migrate documents from one namespace to another.
   */
  migrateDocuments(projectUid, body) {
    const url = `/projects/${projectUid}/documents/migrate`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Create an issue list. API user is set as creator.
   */
  postIssueLists(projectUid, body) {
    const url = `/projects/${projectUid}/issue_lists`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Project additional attachments to a workspace.
   */
  projectAttachmentsIntoWorkspace(workspaceUid, body) {
    const url = `/workspaces/${workspaceUid}/attachments`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Project additional sheet history sets to a workspace.
   */
  projectSheetHistorySetsIntoWorkspace(workspaceUid, body) {
    const url = `/workspaces/${workspaceUid}/sheet_history_sets`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Sets the status of the sheet upload. When "complete", kicks off processing.
   * Details here: https://plangrid.quip.com/6WdKAyvbuiT9
   */
  startSheetProcessing(projectUid, versionUploadUid) {
    const url = `/projects/${projectUid}/sheets/uploads/${versionUploadUid}/completions`;
    return this.raw.post(url, {});
  }
  /**
   * Unproject attachments from a workspace.
   */
  unprojectAttachmentsFromWorkspace(workspaceUid, params) {
    const url = {
      url: `/workspaces/${workspaceUid}/attachments`,
      params: sanitize(params || {}),
    };
    return this.raw.delete(url);
  }
  /**
   * Unproject sheet history sets from a workspace.
   */
  unprojectSheetsFromWorkspace(workspaceUid, params) {
    const url = {
      url: `/workspaces/${workspaceUid}/sheet_history_sets`,
      params: sanitize(params || {}),
    };
    return this.raw.delete(url);
  }
  /**
   * Update the metadata of an attachment.
   */
  updateAttachment(projectUid, attachmentUid, body) {
    const url = `/projects/${projectUid}/attachments/${attachmentUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Updates an issue on a project.
   */
  updateIssue(projectUid, issueUid, body) {
    const url = `/projects/${projectUid}/issues/${issueUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Updates an issue list on a project.
   */
  updateIssueList(projectUid, issueListUid, body) {
    const url = `/projects/${projectUid}/issue_lists/${issueListUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Update attributes of an OAuth app.
   */
  updateOauthApplication(clientId, body) {
    const url = `/oauth/applications/${clientId}`;
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Update photo metadata in a project.
   */
  updatePhoto(projectUid, photoUid, body, params) {
    const url = {
      url: `/projects/${projectUid}/photos/${photoUid}`,
      params: sanitize(params || {}, { extra_fields: "csv" }),
    };
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Modify a project.
   */
  updateProject(projectUid, body) {
    const url = `/projects/${projectUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  updateProjectSheetOrder(projectUid, body) {
    const url = `/projects/${projectUid}/sheet_order`;
    return this.raw.post(url, jsonify(body));
  }
  /**
   * Change a user's role on a project team. API user must have permissions
   * to invite project team members (see [Spec] Permissions, currently only
   * admins can invite).
   */
  updateProjectUser(projectUid, userUid, body) {
    const url = `/projects/${projectUid}/users/${userUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Update an existing RFI. Only fields passed in will be updated. API user
   * is set as modifier in journals and updater on RFI. All notifications are
   * net.
   */
  updateRfi(projectUid, rfiUid, body) {
    const url = `/projects/${projectUid}/rfis/${rfiUid}`;
    return this.raw.patch(url, jsonify(body));
  }
  /**
   * Updates an RFI status on a project.
   */
  updateRfiStatus(projectUid, statusUid, body) {
    const url = `/projects/${projectUid}/rfis/statuses/${statusUid}`;
    return this.raw.patch(url, jsonify(body));
  }
}
